import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCredentialExpiry } from '../hooks/credential-expiry'
import { useAppVersion } from '../hooks/app-version'
import { useRoleSysProvider } from '../../role/hook/role-sys'
import { IconProviderExpiry } from '../icon/icon'

const SESSION_DISMISS_KEY = 'credentialExpiryBannerDismissed'

const isDismissedThisSession = () => {
  try {
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1'
  } catch (e) {
    // Private-mode / storage-blocked browsers: fail open (show the banner)
    // rather than throw during render.
    return false
  }
}

// A provider credential nearing expiry silently breaks sign-in for every
// domain routed to it. This warns a sys-provider admin before that happens,
// the same way UpdateBanner warns about a stale bundle -- fixed, dismissible
// for the session, never automatic.
//
// Bottom-left, same family as UpdateBanner. When that banner might also be
// showing (an update is available), this one sits 88px above bottom:0 so the
// two stack instead of overlapping.
export const CredentialExpiryBanner = () => {
  const roleSysProvider = useRoleSysProvider()
  const soonest = useCredentialExpiry()
  const { updateAvailable } = useAppVersion()

  const [dismissed, setDismissed] = useState(isDismissedThisSession)

  if (!roleSysProvider.accessRead || !soonest || dismissed) return null

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1')
    } catch (e) {
      // Swallow -- dismissal for this render is enough if storage is unavailable.
    }
    setDismissed(true)
  }

  const expired = soonest.daysLeft < 0

  return (
    <div
      className="edxr-notice position-fixed start-0 m-3 p-3 border rounded-3 shadow d-flex align-items-center gap-3"
      style={{ zIndex: 1080, maxWidth: 420, bottom: updateAvailable ? 88 : 0 }}
      role="status"
    >
      <span
        className="bg-warning-subtle text-warning rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 40, height: 40 }}
        aria-hidden="true"
      >
        <IconProviderExpiry />
      </span>

      <div className="small">
        <div className="fw-semibold">
          <strong>{soonest.providerName}</strong>{' '}
          {expired
            ? <React.Fragment>credential expired <strong>{Math.abs(soonest.daysLeft)} days ago</strong></React.Fragment>
            : <React.Fragment>credential expires in <strong>{soonest.daysLeft} days</strong></React.Fragment>}
        </div>
      </div>
      <div className="d-flex gap-2 ms-auto">
        <button type="button" className="btn btn-sm btn-outline-secondary edxr-notice-later" onClick={handleDismiss}>
          Later
        </button>
        <Link className="btn btn-sm btn-primary" to={`/admin/sso/connections/${soonest.id}`}>
          Review
        </Link>
      </div>
    </div>
  )
}
